import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, Pencil, Archive, Download } from "lucide-react";
import { PageContainer } from "@/layout/PageContainer";
import { PageHeader } from "@/ui/PageHeader";
import { StatTile } from "@/ui/StatTile";
import { Card } from "@/ui/Card";
import { Badge } from "@/ui/Badge";
import { Avatar } from "@/ui/Avatar";
import { Button } from "@/ui/Button";
import { SearchInput } from "@/ui/SearchInput";
import { Select } from "@/ui/FormControls";
import { SegmentedFilter, type FilterOption } from "@/ui/SegmentedFilter";
import { DataTable, type Column } from "@/ui/data-table/DataTable";
import { ConfirmDialog } from "@/ui/modals/ConfirmDialog";
import { LoadingState } from "@/ui/feedback/states";
import { ApiFallbackBanner } from "@/ui/feedback/ApiFallbackBanner";
import { toast } from "@/stores/toast.store";
import { getErrorMessage } from "@/lib/api-errors";
import { formatDZD, formatMoney, formatUSD } from "@/lib/formatters";
import { useLabels } from "@/lib/labels";
import type { Client, ClientStatus, ClientType } from "@/types";
import { useClients, useClientMutations } from "./clients.hooks";
import { exportClientsToExcel } from "./clients.export";
import { ClientFormModal, type ClientFormValues } from "./components/ClientFormModal";
import { ClientSocialLinks } from "./components/ClientSocialLinks";

type StatusFilter = "all" | ClientStatus;
type TypeFilter = "all" | ClientType;
type SortMode = "az" | "za" | "newest" | "oldest";

function searchableText(client: Client): string {
  return [client.name, client.email, client.phone, client.niche].filter(Boolean).join(" ").toLowerCase();
}

function sortTime(client: Client): number {
  const raw = client.createdAt ?? client.startDate;
  if (!raw) return 0;
  const time = new Date(raw).getTime();
  return Number.isNaN(time) ? 0 : time;
}

export function ClientsPage() {
  const { t } = useTranslation();
  const { CLIENT_STATUS, CLIENT_TYPE, SERVICE_TYPE, CONTRACT_MODEL } = useLabels();
  const navigate = useNavigate();
  const statusFilters: FilterOption<StatusFilter>[] = [
    { value: "all", label: t("clients.status.all") },
    { value: "ACTIVE", label: t("clients.status.active") },
    { value: "ONBOARDING", label: t("clients.status.onboarding") },
    { value: "PAUSED", label: t("clients.status.paused") },
    { value: "CHURNED", label: t("clients.status.churned") },
  ];
  const { clients, isLoading, isError, refetch } = useClients();
  const mutations = useClientMutations();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [type, setType] = useState<TypeFilter>("all");
  const [niche, setNiche] = useState("all");
  const [sort, setSort] = useState<SortMode>("newest");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Client | undefined>();
  const [toArchive, setToArchive] = useState<Client | undefined>();

  const nicheOptions = useMemo(
    () =>
      Array.from(new Set(clients.map((client) => client.niche?.trim()).filter((value): value is string => Boolean(value))))
        .sort((a, b) => a.localeCompare(b)),
    [clients],
  );

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = clients.filter(
      (client) =>
        (status === "all" || client.status === status) &&
        (type === "all" || client.type === type) &&
        (niche === "all" || client.niche === niche) &&
        (q === "" || searchableText(client).includes(q)),
    );

    return [...filtered].sort((a, b) => {
      if (sort === "az") return a.name.localeCompare(b.name);
      if (sort === "za") return b.name.localeCompare(a.name);
      if (sort === "oldest") return sortTime(a) - sortTime(b);
      return sortTime(b) - sortTime(a);
    });
  }, [clients, niche, search, sort, status, type]);

  const stats = useMemo(() => {
    const activeCount = clients.filter((c) => c.status === "ACTIVE").length;
    const onboardingCount = clients.filter((c) => c.status === "ONBOARDING").length;
    const pausedCount = clients.filter((c) => c.status === "PAUSED").length;
    const churnedCount = clients.filter((c) => c.status === "CHURNED").length;
    const revenue = clients.reduce((s, c) => s + c.revenue, 0);
    const adSpend = clients.reduce((s, c) => s + c.adSpendUsd, 0);
    return { activeCount, onboardingCount, pausedCount, churnedCount, revenue, adSpend };
  }, [clients]);

  const onSave = async (values: ClientFormValues) => {
    try {
      if (editing) {
        await mutations.update(editing.id, values);
        toast.success(t("clients.saveUpdated", { name: values.name }));
      } else {
        await mutations.create(values);
        toast.success(t("clients.saveCreated", { name: values.name }));
      }
      setFormOpen(false);
      setEditing(undefined);
    } catch (err) {
      toast.error(getErrorMessage(err, t("clients.saveFailed")));
    }
  };

  const onArchiveConfirm = async () => {
    if (!toArchive) return;
    try {
      await mutations.archive(toArchive.id);
      toast.success(t("clients.archiveSuccess", { name: toArchive.name }));
    } catch (err) {
      toast.error(getErrorMessage(err, t("clients.archiveFailed")));
    } finally {
      setToArchive(undefined);
    }
  };

  const onExport = () => {
    try {
      exportClientsToExcel(rows, t, { status: CLIENT_STATUS, type: CLIENT_TYPE, contract: CONTRACT_MODEL });
      toast.success(t("clients.exportSuccess"));
    } catch {
      toast.error(t("clients.exportFailed"));
    }
  };

  const columns: Column<Client>[] = [
    {
      key: "client",
      header: t("clients.columns.client"),
      cell: (client) => {
        const detail = [client.email, client.phone, client.niche].filter(Boolean).join(" / ");
        return (
          <div className="flex items-center gap-2.5">
            <Avatar name={client.name} />
            <div className="min-w-0">
              <div className="font-semibold text-text truncate max-w-[220px]">{client.name}</div>
              <div className="text-[10px] text-text3 truncate max-w-[260px]">
                {detail || `${t("clients.since")} ${client.since}`}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      key: "status",
      header: t("clients.columns.status"),
      cell: (client) => (
        <Badge tone={CLIENT_STATUS[client.status].tone} dot>
          {CLIENT_STATUS[client.status].label}
        </Badge>
      ),
    },
    {
      key: "type",
      header: t("clients.columns.type"),
      cell: (client) => <Badge tone={CLIENT_TYPE[client.type].tone}>{CLIENT_TYPE[client.type].label}</Badge>,
    },
    {
      key: "niche",
      header: t("clients.columns.niche"),
      cell: (client) => client.niche ?? <span className="text-text3">-</span>,
    },
    {
      key: "service",
      header: t("clients.columns.service"),
      cell: (client) => <Badge tone={SERVICE_TYPE[client.service].tone}>{SERVICE_TYPE[client.service].label}</Badge>,
    },
    {
      key: "contract",
      header: t("clients.columns.contract"),
      cell: (client) => <Badge tone={CONTRACT_MODEL[client.contractModel].tone}>{CONTRACT_MODEL[client.contractModel].label}</Badge>,
    },
    {
      key: "fee",
      header: t("clients.columns.fee"),
      align: "right",
      cell: (client) =>
        client.monthlyFee > 0 ? (
          <span className="mono">{formatMoney(client.monthlyFee, client.currency ?? "DZD")}</span>
        ) : (
          <span className="text-text3">-</span>
        ),
    },
    {
      key: "social",
      header: t("clients.columns.social"),
      cell: (client) => <ClientSocialLinks client={client} compact />,
    },
    {
      key: "wallet",
      header: t("clients.columns.wallet"),
      align: "right",
      cell: (client) => (
        <span className={`mono ${client.walletUsd < 0 ? "text-danger" : client.walletUsd === 0 ? "text-warning" : ""}`}>
          {formatUSD(client.walletUsd)}
        </span>
      ),
    },
    {
      key: "actions",
      header: t("common.actions"),
      align: "right",
      cell: (client) => (
        <div className="flex items-center justify-end gap-1" onClick={(event) => event.stopPropagation()}>
          <Button
            variant="ghost"
            size="xs"
            aria-label={t("clients.editClient", { name: client.name })}
            title={t("clients.editClient", { name: client.name })}
            onClick={() => {
              setEditing(client);
              setFormOpen(true);
            }}
          >
            <Pencil className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="xs"
            className="text-danger"
            aria-label={t("clients.archiveClient", { name: client.name })}
            title={t("clients.archiveClient", { name: client.name })}
            onClick={() => setToArchive(client)}
          >
            <Archive className="h-3 w-3" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        title={t("clients.title")}
        subtitle={t("clients.subtitle", { count: clients.length, active: stats.activeCount })}
        actions={
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={onExport} disabled={rows.length === 0}>
              <Download className="h-3 w-3" /> {t("clients.exportExcel")}
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setEditing(undefined);
                setFormOpen(true);
              }}
            >
              <Plus className="h-3 w-3" /> {t("clients.add")}
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 mb-5">
        <StatTile label={t("clients.status.active")} value={stats.activeCount} tone="success" note={formatDZD(stats.revenue)} />
        <StatTile label={t("clients.status.onboarding")} value={stats.onboardingCount} tone="info" />
        <StatTile label={t("clients.status.paused")} value={stats.pausedCount} tone="warning" note={formatUSD(stats.adSpend)} />
        <StatTile label={t("clients.status.churned")} value={stats.churnedCount} tone="danger" />
      </div>

      {isError && <ApiFallbackBanner onRetry={() => refetch()} />}

      <div className="space-y-3 mb-3.5">
        <div className="grid grid-cols-1 md:grid-cols-[minmax(220px,1fr)_170px_170px_170px] gap-3">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={t("clients.searchByNameEmailPhoneNiche")}
          />
          <Select value={type} aria-label={t("clients.filters.type")} onChange={(event) => setType(event.target.value as TypeFilter)}>
            <option value="all">{t("clients.filters.allTypes")}</option>
            {(Object.keys(CLIENT_TYPE) as ClientType[]).map((value) => (
              <option key={value} value={value}>
                {CLIENT_TYPE[value].label}
              </option>
            ))}
          </Select>
          <Select value={niche} aria-label={t("clients.filters.niche")} onChange={(event) => setNiche(event.target.value)}>
            <option value="all">{t("clients.filters.allNiches")}</option>
            {nicheOptions.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </Select>
          <Select value={sort} aria-label={t("clients.sort.label")} onChange={(event) => setSort(event.target.value as SortMode)}>
            <option value="az">{t("clients.sort.az")}</option>
            <option value="za">{t("clients.sort.za")}</option>
            <option value="newest">{t("clients.sort.newest")}</option>
            <option value="oldest">{t("clients.sort.oldest")}</option>
          </Select>
        </div>
        <SegmentedFilter options={statusFilters} value={status} onChange={setStatus} />
      </div>

      <Card>
        {isLoading ? (
          <LoadingState rows={6} />
        ) : (
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(client) => client.id}
            onRowClick={(client) => navigate(`/clients/${client.id}`)}
            empty={{
              title: t("clients.empty.title"),
              message: t("clients.empty.message"),
              action: (
                <Button
                  size="sm"
                  onClick={() => {
                    setEditing(undefined);
                    setFormOpen(true);
                  }}
                >
                  <Plus className="h-3 w-3" /> {t("clients.add")}
                </Button>
              ),
            }}
          />
        )}
      </Card>

      <ClientFormModal
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditing(undefined);
        }}
        onSave={onSave}
        initial={editing}
      />

      <ConfirmDialog
        open={!!toArchive}
        onClose={() => setToArchive(undefined)}
        title={t("clients.archiveTitle")}
        message={t("clients.archiveMessage", { name: toArchive?.name ?? "" })}
        confirmLabel={t("common.archive")}
        onConfirm={onArchiveConfirm}
      />
    </PageContainer>
  );
}
